/**
 * The front door illustration: what actually happens to an application.
 *
 * A stock photograph would say nothing. This draws the thing the product is
 * for. An applicant arriving here has never been shown the statutory period as
 * a shape before, and the whole argument of the system is that the period is
 * real, countable and theirs. So the hero is the period.
 *
 * Two drawings, because orientation follows the canvas. The wide one is a
 * timeline for a desktop hero; the tall one is a rail for a phone. Each is
 * designed at its own size rather than one being squeezed into the other.
 *
 * White line work on black. Plain paths, rects, lines and text: no pattern, no
 * gradient, no filter, so it survives a print pipeline unchanged.
 */

import type { ReactElement } from "react";
import type { Bilingual, Lang } from "@ksidc/ontology";
import { bi } from "@ksidc/client";

const STOPS: { label: Bilingual; cite: string }[] = [
  { label: bi("Received", "ലഭിച്ചു"), cite: "s.10" },
  { label: bi("Registered", "രജിസ്റ്റർ ചെയ്തു"), cite: "Form B" },
  { label: bi("Site inspected", "സ്ഥലം പരിശോധിച്ചു"), cite: "r.8" },
  { label: bi("Sent to offices", "ഓഫീസുകൾക്ക് അയച്ചു"), cite: "r.5" },
  { label: bi("Decided", "തീരുമാനിച്ചു"), cite: "r.10" },
];

const DEEMED = bi("Counts as given", "നൽകിയതായി കണക്കാക്കും");
const PERIOD = bi(
  "The Board has 30 days to decide.",
  "തീരുമാനം എടുക്കാൻ ബോർഡിന് 30 ദിവസമുണ്ട്.",
);
const DAY0 = bi("Day 0", "ദിവസം 0");
const DAY30 = bi("Day 30", "ദിവസം 30");

const pick = (t: Bilingual, lang: Lang): string => (lang === "ml" ? t.ml || t.en : t.en);

/** The wide drawing. A timeline, for a desktop hero. */
function Wide({ lang }: { lang: Lang }): ReactElement {
  const x0 = 42;
  const x1 = 598;
  const railY = 132;
  const step = (x1 - x0) / (STOPS.length - 1);

  return (
    <svg
      viewBox="0 0 640 260"
      role="img"
      aria-label={pick(PERIOD, lang)}
      className="journey journey-wide"
    >
      {/* The span of the period, drawn as the thing that contains everything. */}
      <line x1={x0} y1={62} x2={x0} y2={196} stroke="currentColor" strokeWidth="1" opacity="0.5" />
      <line x1={x1} y1={62} x2={x1} y2={196} stroke="currentColor" strokeWidth="1" opacity="0.5" />

      <text x={x0} y={48} className="j-axis" textAnchor="start">
        {pick(DAY0, lang)}
      </text>
      <text x={x1} y={48} className="j-axis" textAnchor="end">
        {pick(DAY30, lang)}
      </text>

      {/* The rail. */}
      <line x1={x0} y1={railY} x2={x1} y2={railY} stroke="currentColor" strokeWidth="1.25" />

      {STOPS.map((s, i) => {
        const x = x0 + step * i;
        const above = i % 2 === 0;
        return (
          <g key={s.cite}>
            <circle
              cx={x}
              cy={railY}
              r={i === 0 ? 7 : 5}
              fill={i === 0 ? "currentColor" : "none"}
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <line
              x1={x}
              y1={above ? railY - 12 : railY + 12}
              x2={x}
              y2={above ? railY - 30 : railY + 30}
              stroke="currentColor"
              strokeWidth="1"
              opacity="0.55"
            />
            <text
              x={x}
              y={above ? railY - 40 : railY + 46}
              className="j-label"
              textAnchor="middle"
            >
              {pick(s.label, lang)}
            </text>
            <text
              x={x}
              y={above ? railY - 26 : railY + 60}
              className="j-cite"
              textAnchor="middle"
            >
              {s.cite}
            </text>
          </g>
        );
      })}

      {/* What happens at the end of the period, which is the point. */}
      <g>
        <line x1={x1} y1={196} x2={x1} y2={222} stroke="currentColor" strokeWidth="1" />
        <text x={x1} y={238} className="j-deemed" textAnchor="end">
          {pick(DEEMED, lang)} · s.10
        </text>
      </g>

      <text x={x0} y={222} className="j-axis" textAnchor="start">
        {pick(PERIOD, lang)}
      </text>
    </svg>
  );
}

/** The tall drawing. A rail, for a phone. */
function Tall({ lang }: { lang: Lang }): ReactElement {
  const railX = 26;
  const y0 = 34;
  const step = 46;

  return (
    <svg
      viewBox="0 0 320 300"
      role="img"
      aria-label={pick(PERIOD, lang)}
      className="journey journey-tall"
    >
      <text x={0} y={16} className="j-axis">
        {pick(DAY0, lang)}
      </text>

      <line
        x1={railX}
        y1={y0}
        x2={railX}
        y2={y0 + step * (STOPS.length - 1)}
        stroke="currentColor"
        strokeWidth="1.25"
      />

      {STOPS.map((s, i) => {
        const y = y0 + step * i;
        return (
          <g key={s.cite}>
            <circle
              cx={railX}
              cy={y}
              r={i === 0 ? 7 : 5}
              fill={i === 0 ? "currentColor" : "none"}
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <text x={railX + 18} y={y - 1} className="j-label">
              {pick(s.label, lang)}
            </text>
            <text x={railX + 18} y={y + 13} className="j-cite">
              {s.cite}
            </text>
          </g>
        );
      })}

      <line
        x1={railX}
        y1={y0 + step * (STOPS.length - 1)}
        x2={railX}
        y2={y0 + step * (STOPS.length - 1) + 26}
        stroke="currentColor"
        strokeWidth="1"
        strokeDasharray="3 3"
      />
      <text x={0} y={y0 + step * (STOPS.length - 1) + 48} className="j-axis">
        {pick(DAY30, lang)}
      </text>
      <text x={0} y={y0 + step * (STOPS.length - 1) + 64} className="j-deemed">
        {pick(DEEMED, lang)} · s.10
      </text>
    </svg>
  );
}

export function StatutoryJourney({ lang }: { lang: Lang }): ReactElement {
  return (
    <div className="journey-wrap" aria-hidden={false}>
      <Wide lang={lang} />
      <Tall lang={lang} />
    </div>
  );
}
